import { type DeepPartial, recordOf } from "#src/types/utils.ts";
import {
  KEYBOARD_BINDING_MAX_COMBOS,
  KEYBOARD_COMBO_MAX_LENGTH,
  type KeyboardShortcutId,
  keyboardShortcutIds,
} from "#src/types/keyboardShortcuts.ts";
import { reminderChannels } from "#src/types/calendarEvent.ts";
import { z } from "zod";

/*
 * User preferences are defined generically with a schema, see UserPropertyValueSchema.
 * The schema describes the possible values for all preferences and has other descriptive information.
 * It can be used to validate a user's preferences, and makes it easier to validate preferences when updating them.
 * You can think of it as an opinionated subset of a Zod schema - we support a small subset of Zod features,
 * and we require a very strict shape that's hard to enforce with Zod.
 *
 * The schema is static. The things we store in the DB are preference instances. That type can be derived from the schema types.
 *
 * There's a lot of constants in here that we might put elsewhere, it's not really shared with web, but the types/ dir can't depend on other files
 * without breaking web, so we just dump everything in here.
 */

/* -------- Abstract schema types -------- */

/** General schema for any preference property schema. */
export type UiPropertyValueSchema =
  | {
      propertyType: "enum";
      values: Set<string>;
      default: string;
      description: string;
    }
  | {
      propertyType: "number";
      min: number;
      max: number;
      default: number;
      description: string;
    }
  | {
      propertyType: "string";
      default: string;
      description: string;
      /** Optional character cap for free-form text values; enforced by `updateUserPreferences`. */
      maxLength?: number;
      /**
       * When true, `updateUserPreferences` strips `<` / `>` from the value
       * before storing. Used for free-form text that gets wrapped in an XML
       * wire tag downstream (see `customInstructions`) so a direct API call
       * can't smuggle in a closing tag and break out of the wrapper.
       */
      stripAngleBrackets?: boolean;
    }
  | {
      propertyType: "boolean";
      default: boolean;
      description: string;
    }
  | {
      /**
       * A list of short strings, or `null` for "not set". The leaf is nullable
       * because a stored value can only ever be replaced, never removed: the
       * update endpoint deep-merges a delta, and JSON has no way to carry
       * "delete this key". Readers treat `null` like an absent value and fall
       * back to their own default. Arrays are leaves to the merge, so a delta
       * replaces the whole list rather than merging element by element.
       */
      propertyType: "stringArray";
      default: string[] | null;
      maxItems: number;
      maxItemLength: number;
      description: string;
    }
  | {
      // A nested group of properties — one more level of structure within a
      // surface (e.g. notifications → per-tier channel toggles). The framework
      // validates and deep-merges these by recursing through `properties`;
      // there is no top-level `default` (each leaf carries its own).
      propertyType: "object";
      description: string;
      properties: Record<string, UiPropertyValueSchema>;
    };

/** For a property schema, extract the type of the value. */
type ExtractPropertyType<T> = T extends { propertyType: "enum"; values: Set<infer U> }
  ? U
  : T extends { propertyType: "number" }
    ? number
    : T extends { propertyType: "string" }
      ? string
      : T extends { propertyType: "boolean" }
        ? boolean
        : T extends { propertyType: "stringArray" }
          ? string[] | null
          : T extends { propertyType: "object"; properties: infer P }
            ? { -readonly [K in keyof P]: ExtractPropertyType<P[K]> }
            : never;

/** The schema for a single surface. */
export type SurfaceSchema = {
  description: string;
  properties: Record<string, UiPropertyValueSchema>;
};

/** For a SurfaceSchema, extract all of the the property values. */
type SurfaceSchemaProperties<T extends SurfaceSchema> = {
  -readonly [K in keyof T["properties"]]: ExtractPropertyType<T["properties"][K]>;
};

/** The schema for all surfaces. */
export type PreferencesSchema<Surfaces extends string> = Record<Surfaces, SurfaceSchema>;

/** For a PreferenceSchema, extract all of the property values for all of the surfaces. */
export type PreferencesInstance<T extends PreferencesSchema<string>> = {
  -readonly [K in keyof T]: SurfaceSchemaProperties<T[K]>;
};

/** Like @see PreferencesInstance, but partial - matches the actual shape of the DB. */
export type PreferencesInstancePartial<T extends PreferencesSchema<string>> = DeepPartial<
  PreferencesInstance<T>
>;

/* -------- Concrete Schemas -------- */

export const homeLayoutModePropertyValues = ["large", "compact"] as const;
export type HomeLayoutMode = (typeof homeLayoutModePropertyValues)[number];
const homeUiPreferencesSchema = {
  description: "Preferences for the home page",
  properties: {
    layoutMode: {
      propertyType: "enum",
      values: new Set(homeLayoutModePropertyValues),
      default: "compact",
      description: "Preferred layout mode for the home page.",
    },
    /**
     * Do not name this `sortMode`: `accounts.preferences` still holds
     * `home.sortMode` values from the status-bucket sort (#4479, never
     * migrated), and reusing the key would start honouring them.
     */
    needsAttentionFirst: {
      propertyType: "boolean",
      default: false,
      description: "Show the threads waiting on the user above the rest of the list.",
    },
  },
} as const satisfies SurfaceSchema;

export const feedLayoutModePropertyValues = ["large", "compact", "summary"] as const;
export const feedLayoutModeProperty = {
  propertyType: "enum",
  values: new Set(feedLayoutModePropertyValues),
  default: "large",
  description: "Preferred layout mode for a category feed.",
} as const satisfies UiPropertyValueSchema;
export type FeedLayoutMode = (typeof feedLayoutModePropertyValues)[number];

const globalUiPreferencesSchema = {
  description: "Preferences shared across the app",
  properties: {
    sendInBackground: {
      propertyType: "boolean",
      default: false,
      description:
        "When enabled, sending a message that starts a new thread keeps the user where they are (confirmation toast) instead of opening the new thread. Cmd/Ctrl+Enter always does the opposite.",
    },
  },
} as const satisfies SurfaceSchema;

const calendarsPreferencesSchema = {
  description: "Preferences for calendar selection and management",
  properties: {
    selectedCalendarIds: {
      propertyType: "string",
      default: "[]",
      description: "JSON array of calendar IDs selected for scheduling availability",
    },
    visibleCalendarIds: {
      propertyType: "string",
      default: "",
      description:
        "JSON array of calendar IDs visible in the calendar view. Empty string means use selectedCalendarIds as default.",
    },
    viewType: {
      propertyType: "enum",
      values: new Set(["day", "week", "month"]),
      default: "month",
      description: "The currently selected calendar view type",
    },
    /**
     * Not in the agent's settings allowlist: the agent setting it would skip the
     * question scheduling asks a v3 account, the only place that account is
     * asked which calendars count.
     */
    busyCalendarsAsked: {
      propertyType: "boolean",
      default: false,
      description:
        "Whether the user has been asked which calendars count as busy, or has chosen them",
    },
  },
} as const satisfies SurfaceSchema;

const locationPreferencesSchema = {
  description: "Preferences for location-based settings",
  properties: {
    timezonePreference: {
      propertyType: "enum",
      values: new Set(["home", "current"]),
      default: "current",
      description: "Whether to use home timezone or current timezone",
    },
  },
} as const satisfies SurfaceSchema;

const notificationsPreferencesSchema = {
  description: "Preferences for notification settings",
  properties: {
    // Deprecated, read by nothing: the "Texts from Okthx" switch, replaced by
    // `reminders.sms`. Kept so a shipped app that still writes it gets a 200
    // rather than a 400.
    smsEnabled: {
      propertyType: "boolean",
      default: true,
      description: "Deprecated. Use notifications.reminders.sms.",
    },
    // Global switch for phone (Expo) push, over every category's push toggle.
    // Browser push is not covered: it stays on the per-category toggles.
    mobilePush: {
      propertyType: "boolean",
      default: true,
      description: "Receive push notifications on your phone",
    },
    // Channels for notifications the agent needs you to act on (questions,
    // approvals). Push, badge, and email on by default; SMS and WhatsApp stay opt-in.
    needsAttention: {
      propertyType: "object",
      description: "Channels for notifications that need your attention",
      properties: {
        enabled: {
          propertyType: "boolean",
          default: true,
          description: "Master switch for all notifications that need your attention",
        },
        push: {
          propertyType: "boolean",
          default: true,
          description: "Receive push notifications on mobile and in the browser",
        },
        inAppBadge: {
          propertyType: "boolean",
          default: true,
          description:
            "Show an unread/attention badge on the app icon, browser tab, and chat panel",
        },
        email: {
          propertyType: "boolean",
          default: true,
          description: "Receive email notifications",
        },
        sms: {
          propertyType: "boolean",
          default: false,
          description: "Receive SMS notifications (requires a verified phone number)",
        },
        whatsapp: {
          propertyType: "boolean",
          default: false,
          description: "Receive WhatsApp messages from Okthx (requires a linked WhatsApp)",
        },
      },
    },
    // Channels for heads-up notifications about work that finished. Push and
    // badge are on by default; email, SMS and WhatsApp are off, since a notification for
    // every completion would be too noisy in the inbox.
    fyi: {
      propertyType: "object",
      description: "Channels for heads-up (FYI) notifications",
      properties: {
        enabled: {
          propertyType: "boolean",
          default: true,
          description: "Master switch for all heads-up (FYI) notifications",
        },
        push: {
          propertyType: "boolean",
          default: true,
          description: "Receive push notifications on mobile and in the browser",
        },
        inAppBadge: {
          propertyType: "boolean",
          default: true,
          description:
            "Show an unread/attention badge on the app icon, browser tab, and chat panel",
        },
        email: {
          propertyType: "boolean",
          default: false,
          description: "Receive email notifications",
        },
        sms: {
          propertyType: "boolean",
          default: false,
          description: "Receive SMS notifications (requires a verified phone number)",
        },
        whatsapp: {
          propertyType: "boolean",
          default: false,
          description: "Receive WhatsApp messages from Okthx (requires a linked WhatsApp)",
        },
      },
    },
    // When the agent needs you and you haven't responded, follow up the
    // baseline notification with a text reminder up an escalation ladder. Needs
    // Attention only; off by default.
    escalation: {
      propertyType: "boolean",
      default: false,
      description:
        "Follow up with a text reminder if you don't respond to something that needs you",
    },
    // Escalation rung delays, in minutes (only used when `escalation` is on).
    // First reminder after the baseline; second after the first.
    escalationFirstDelay: {
      propertyType: "enum",
      values: new Set(["5", "15", "30", "60"]),
      default: "30",
      description: "Minutes to wait before the first follow-up text reminder",
    },
    escalationSecondDelay: {
      propertyType: "enum",
      values: new Set(["60", "120", "180"]),
      default: "120",
      description: "Minutes after the first reminder before the second",
    },
    // Channels for calendar event reminders (BIG-5612). Same shape as the tiers
    // above: a master switch plus per-channel toggles. On by default with push
    // only; email, text and WhatsApp stay opt-in (text needs a verified phone).
    calendarReminders: {
      propertyType: "object",
      description: "Channels for calendar event reminders",
      properties: {
        enabled: {
          propertyType: "boolean",
          default: true,
          description: "Master switch for calendar event reminder notifications",
        },
        push: {
          propertyType: "boolean",
          default: true,
          description: "Receive push notifications on mobile and in the browser",
        },
        email: {
          propertyType: "boolean",
          default: false,
          description: "Receive email notifications",
        },
        sms: {
          propertyType: "boolean",
          default: false,
          description: "Receive SMS notifications (requires a verified phone number)",
        },
        whatsapp: {
          propertyType: "boolean",
          default: false,
          description: "Receive WhatsApp messages from Okthx (requires a linked WhatsApp)",
        },
      },
    },
    // Channels for usage-allowance alerts (BIG-7430): the warning when usage
    // approaches the monthly ceiling and the notice when it's exhausted. Same
    // shape as the tiers above. Email and SMS on by default — these fire when
    // the user is NOT in the app, so the durable/reachable channels lead and
    // push stays opt-in. `enabled` gates only the approaching-limit warning;
    // the exhausted notice always sends (the assistant has stopped working,
    // which is billing-critical), honoring just the per-channel toggles.
    usageAlerts: {
      propertyType: "object",
      description: "Channels for usage-allowance alerts",
      properties: {
        enabled: {
          propertyType: "boolean",
          default: true,
          description: "Warn when you approach your monthly usage allowance",
        },
        push: {
          propertyType: "boolean",
          default: false,
          description: "Receive push notifications on mobile and in the browser",
        },
        email: {
          propertyType: "boolean",
          default: true,
          description: "Receive email notifications",
        },
        sms: {
          propertyType: "boolean",
          default: true,
          description: "Receive SMS notifications (requires a verified phone number)",
        },
      },
    },
    // The channel a new okthx reminder takes when the user doesn't say, while
    // the `reminder-channels` gate is closed: email if saved, otherwise text.
    // With the gate open the `reminders` switches decide and this is read by
    // nothing. The wider values are accepted because a shipped app writes them
    // while the gate is open; they read as text. Resolved when the reminder is
    // created, so changing it cannot repoint a reminder the user was told about.
    reminderDelivery: {
      propertyType: "enum",
      values: new Set([...reminderChannels, "auto"]),
      default: "text",
      description: "Default channel for new reminders: text or email",
    },
    // The channels an okthx reminder may go out on. Each reminder still takes
    // one: the conversation it was asked in if that channel is on, else the
    // first one on — see `pickReminderChannel`. A channel the user names for a
    // reminder is used even when it is off here. Unrelated to
    // `calendarReminders`, the lead-time alerts for ordinary calendar events.
    reminders: {
      propertyType: "object",
      description: "Channels Okthx may send your reminders on",
      properties: {
        sms: {
          propertyType: "boolean",
          default: true,
          description:
            "Let new reminders go by text (SMS/iMessage) when you don't name a channel. Reminders already set keep theirs.",
        },
        whatsapp: {
          propertyType: "boolean",
          default: true,
          description:
            "Let new reminders go on WhatsApp when you don't name a channel. Reminders already set keep theirs.",
        },
        app: {
          propertyType: "boolean",
          default: true,
          description:
            "Let new reminders go as a push notification in the Okthx app or browser when you don't name a channel. Reminders already set keep theirs.",
        },
        email: {
          propertyType: "boolean",
          default: true,
          description:
            "Let new reminders go by email when you don't name a channel. Email still takes a reminder when no channel left on reaches you.",
        },
      },
    },
    // The channels `notifications.NOTIFY` / `NOTIFY_ARTIFACT` may use — routine
    // results and anything else the agent sends the user on their own channels.
    // Read by those words alone (`resolveNotifyChannels`). A channel switched
    // off here is dropped from the request; when that leaves nothing, the word
    // falls back to the first of email, text, push and Slack that is on and
    // connected. `sms` governs the `text` channel. Phone and WhatsApp have no
    // switch: no system routine uses them, so they are always allowed.
    routineNotifications: {
      propertyType: "object",
      description:
        "Channels Okthx may use for routine results and other notifications it sends you",
      properties: {
        email: {
          propertyType: "boolean",
          default: true,
          description: "Let Okthx send you routine results and notifications by email",
        },
        sms: {
          propertyType: "boolean",
          default: true,
          description: "Let Okthx text you routine results and notifications (SMS/iMessage)",
        },
        push: {
          propertyType: "boolean",
          default: true,
          description: "Let Okthx send you routine results and notifications as push notifications",
        },
        slack: {
          propertyType: "boolean",
          default: true,
          description: "Let Okthx send you routine results and notifications on Slack",
        },
      },
    },
    quietHoursEnabled: {
      propertyType: "boolean",
      default: false,
      description: "Hold interruptive notifications (push and SMS) during quiet hours",
    },
    quietHoursStart: {
      propertyType: "string",
      default: "21:00",
      description: "Quiet hours start, HH:mm in the user's timezone",
    },
    quietHoursEnd: {
      propertyType: "string",
      default: "08:00",
      description: "Quiet hours end, HH:mm in the user's timezone",
    },
  },
} as const satisfies SurfaceSchema;

// TODO(M1): Rename calendarAccount/defaultCalendarAccountId to calendarConnector/defaultCalendarConnectorId (requires data migration)
const calendarAccountPreferencesSchema = {
  description: "Preferences for calendar account defaults",
  properties: {
    defaultCalendarAccountId: {
      propertyType: "string",
      default: "",
      description:
        "Account ID for the default calendar to create events on. Empty means use primary.",
    },
  },
} as const satisfies SurfaceSchema;

const keyboardBindingProperty = {
  propertyType: "stringArray",
  default: null,
  maxItems: KEYBOARD_BINDING_MAX_COMBOS,
  maxItemLength: KEYBOARD_COMBO_MAX_LENGTH,
  description:
    "The key combos bound to this shortcut, in react-hotkeys-hook syntax (`mod+k`, `g c`). null uses the app's default; an empty list disables the shortcut.",
} as const satisfies UiPropertyValueSchema;

// One nullable list per shortcut id, so the server rejects an id the app does
// not have and the client reads every binding by a typed key.
const keyboardBindingProperties: Record<KeyboardShortcutId, typeof keyboardBindingProperty> =
  recordOf(keyboardShortcutIds, () => keyboardBindingProperty);

const keyboardPreferencesSchema = {
  description: "Preferences for keyboard shortcuts",
  properties: {
    shortcutsEnabled: {
      propertyType: "enum",
      values: new Set(["enabled", "disabled"]),
      default: "enabled",
      description: "Whether keyboard shortcuts are enabled (e.g., j/k navigation, Cmd+D archive)",
    },
    bindings: {
      propertyType: "object",
      description: "Per-shortcut key overrides, keyed by shortcut id",
      properties: keyboardBindingProperties,
    },
  },
} as const satisfies SurfaceSchema;

export const bufferStyleValues = ["backToBack", "buffersAfterEach", "buffersAfterXHours"] as const;
export type BufferStyle = (typeof bufferStyleValues)[number];

export const CUSTOM_INSTRUCTIONS_MAX_LENGTH = 2000;
export const COMMUNICATIONS_INSTRUCTIONS_MAX_LENGTH = 2000;

const agentPreferencesSchema = {
  description: "Preferences that shape the AI assistant's behavior",
  properties: {
    customInstructions: {
      propertyType: "string",
      default: "",
      maxLength: CUSTOM_INSTRUCTIONS_MAX_LENGTH,
      stripAngleBrackets: true,
      description:
        "Free-form standing guidance the assistant should honor on every run (tone, format, hard preferences). Cannot grant tool authority or override safety rules.",
    },
  },
} as const satisfies SurfaceSchema;

const communicationsPreferencesSchema = {
  description: "Preferences that shape generated communications (emails, invitations)",
  properties: {
    customInstructions: {
      propertyType: "string",
      default: "",
      maxLength: COMMUNICATIONS_INSTRUCTIONS_MAX_LENGTH,
      stripAngleBrackets: true,
      description:
        "Free-form standing guidance for any communication the assistant generates on the user's behalf — emails and meeting invitations. Tone, salutations, signoffs, formatting preferences. Cannot grant tool authority or override safety rules.",
    },
    saveDraftsToMailbox: {
      propertyType: "boolean",
      default: true,
      description:
        "When enabled, an email draft awaiting the user's review is also written into their mailbox's Drafts (Gmail or Outlook, per the sending connector), so they can review and send it from their email app. Turning it off keeps drafts in Okthx only.",
    },
    showPromoFooterOnReplies: {
      propertyType: "boolean",
      default: true,
      description:
        "When enabled, an email the assistant sends to another person ends with the small 'Unlock your Okthx' promo footer. Turning it off sends with no Okthx branding at all. Reaches every email the assistant sends on the user's behalf, whether or not a routine produced it. Does not reach notifications and routine reports sent to the user themselves, which carry a footer naming the routine that wrote them.",
    },
  },
} as const satisfies SurfaceSchema;

const schedulingPreferencesSchema = {
  description: "Preferences for scheduling behavior and protected meetings",
  properties: {
    flexibleMeetingIds: {
      propertyType: "string",
      default: "[]",
      description:
        "JSON array of externalRecurringEventId strings for meetings the user has marked as flexible. All recurring meetings are protected by default; only those listed here become soft-busy.",
    },
    bufferStyle: {
      propertyType: "enum",
      values: new Set(bufferStyleValues),
      default: "backToBack",
      description: "How to handle buffer time between meetings",
    },
    bufferHours: {
      propertyType: "number",
      min: 1,
      // max is exclusive in the preference validator, so 9 is needed to allow selecting 8 hours.
      max: 9,
      default: 2,
      description: "Number of hours of consecutive meetings before adding a buffer",
    },
    travelBuffer: {
      propertyType: "boolean",
      default: false,
      description: "Whether to add travel buffer time for in-person meetings",
    },
    travelBufferMinutes: {
      propertyType: "number",
      min: 5,
      max: 120,
      default: 15,
      description:
        "Minutes of travel buffer to add before and after in-person meetings when travelBuffer is enabled",
    },
    oneOnOnePacking: {
      propertyType: "boolean",
      default: false,
      description: "Whether to pack one-on-one meetings together",
    },
    ignoreAutoGeneratedEventConflicts: {
      propertyType: "boolean",
      default: false,
      description:
        "When enabled, calendar events Google auto-creates from Gmail (flights, reservations, deliveries, Airbnb guest stays) are not treated as conflicts when scheduling or showing availability.",
    },
    createVirtualLinksForInPersonEvents: {
      propertyType: "boolean",
      default: true,
      description:
        "Whether new events that have a physical location still get a virtual meeting link (Zoom, Google Meet, Teams). When disabled, a link the user explicitly asked for is still added; only the automatic one is dropped.",
    },
  },
} as const satisfies SurfaceSchema;

const onboardingPreferencesSchema = {
  description: "Preferences for how Okthx helps a new user get set up",
  properties: {
    connectorPromptPopup: {
      propertyType: "boolean",
      default: true,
      description:
        "Connector Prompt Pop-up: the pop-up in the app that asks the user to connect their calendar, mail and other services. Turn off to stop it from showing.",
    },
    pushPromptPopup: {
      propertyType: "boolean",
      default: true,
      description:
        "Push Prompt Pop-up: the pop-up that asks the user to turn on notifications or get the iPhone app. Turn off to stop it from showing.",
    },
  },
} as const satisfies SurfaceSchema;

/**
 * How the tasks list is drawn, at two densities: `rows`, one card per task,
 * and `lines`, one line per task. Triage is deliberately not among them. It
 * is a different job, deciding one task at a time, and it ends; these do not.
 *
 * Named for the shape each one draws rather than for what the menu calls it,
 * so relabelling the menu cannot repoint a stored preference at the other
 * mode. See the `home.sortMode` note above for what reusing a name costs.
 */
export const tasksViewModePropertyValues = ["rows", "lines"] as const;
export type TasksViewMode = (typeof tasksViewModePropertyValues)[number];
/** The default, in one place for the schema and the clients that read it. */
export const TASKS_VIEW_MODE_DEFAULT = "rows" satisfies TasksViewMode;
/**
 * Whether Triage carries on past the suggestions into the reader's own open
 * tasks.
 *
 * Scoped to those deliberately. Suggestions are okthx asking whether something
 * is a task at all — a bounded queue, answered one at a time — so they are
 * always dealt as cards and this says nothing about them. The open list is the
 * reader's own work, and whether they want to meet it a card at a time is a
 * real preference.
 *
 * Off by default. A reader who has never asked for it gets the suggestions,
 * which is the part worth meeting this way, and then lands in their list; the
 * prompt at that boundary is what offers them the rest.
 */
export const TRIAGE_ENABLED_DEFAULT = false;
/**
 * Whether the reader has answered the offer to carry Triage past the
 * suggestions into the rest of their open list.
 *
 * Answered, not shown. A prompt the reader let go is not a decision — reading
 * it as one makes a hint nobody ever sees twice — so only taking it up, or
 * turning Triage off while it is on screen, sets this.
 */
export const TRIAGE_HINT_SEEN_DEFAULT = false;
/** The retired offer flag's default, kept while older native builds still send it. */
export const TRIAGE_OFFER_ANSWERED_DEFAULT = false;

const tasksUiPreferencesSchema = {
  description: "Preferences for the tasks page",
  properties: {
    viewMode: {
      propertyType: "enum",
      values: new Set(tasksViewModePropertyValues),
      default: TASKS_VIEW_MODE_DEFAULT,
      description:
        "Whether tasks are drawn as a list of cards (rows) or one line per task (lines). A working-style preference, so it is held per user and does not change with the filter. Triage is not one of these: it is its own setting, drawn over whichever of these the reader chose.",
    },
    triageEnabled: {
      propertyType: "boolean",
      default: TRIAGE_ENABLED_DEFAULT,
      description:
        "Whether the tasks list is dealt as a stack of cards, suggestions included. Off by default: the list is rows until the user asks for the stack. Independent of viewMode, which says how the list itself is drawn and is where the reader lands once Triage is done.",
    },
    triageHintSeen: {
      propertyType: "boolean",
      default: TRIAGE_HINT_SEEN_DEFAULT,
      description:
        "Whether the one-time hint pointing at the Triage toggle has been shown. Written the first time the tasks screen opens, so a user meets it once.",
    },
    // TODO(BIG-8783): drop once no shipped native build writes it. A key the
    // schema does not list fails the whole write, and builds before the hint
    // send this with every Triage toggle.
    triageOfferAnswered: {
      propertyType: "boolean",
      default: TRIAGE_OFFER_ANSWERED_DEFAULT,
      description:
        "Retired. Older native builds send it with every Triage toggle; it is accepted and read by nothing.",
    },
  },
} as const satisfies SurfaceSchema;

const allUiSurfaces = ["home", "global", "tasks"] as const;
const functionalPreferences = [
  "calendars",
  "location",
  "notifications",
  "calendarAccount",
  "keyboard",
  "scheduling",
  "agent",
  "communications",
  "onboarding",
] as const;
const allSurfaces = [...allUiSurfaces, ...functionalPreferences] as const;

/**
 * All supported user preference surfaces/categories.
 * Note: "surfaces" includes both UI surfaces (home, newsFeed, etc.)
 * and functional preference categories (calendars).
 */
export type UserPreferenceSurfaces = (typeof allSurfaces)[number];

/**
 * The schema for all user preferences.
 * This doesn't match the shape in the DB, it's a schema that describes the possible values for all preferences.
 */
export const userPreferencesSchema = {
  home: homeUiPreferencesSchema,
  global: globalUiPreferencesSchema,
  tasks: tasksUiPreferencesSchema,
  calendars: calendarsPreferencesSchema,
  location: locationPreferencesSchema,
  notifications: notificationsPreferencesSchema,
  calendarAccount: calendarAccountPreferencesSchema,
  keyboard: keyboardPreferencesSchema,
  scheduling: schedulingPreferencesSchema,
  agent: agentPreferencesSchema,
  communications: communicationsPreferencesSchema,
  onboarding: onboardingPreferencesSchema,
} satisfies PreferencesSchema<UserPreferenceSurfaces>;

/** The schemas for all user preferences, by surface. */
export type UserPreferencesSchema = typeof userPreferencesSchema;

/** The type of the preference object we'll store in the DB, as derived from the schema. */
export type UserPreferences = PreferencesInstancePartial<typeof userPreferencesSchema>;

/** Like @UserPreferences, but not partial. Useful for getting individual property values. */
export type UserPreferencesRequired = PreferencesInstance<typeof userPreferencesSchema>;

/* -------- API Types/Schemas -------- */

/** GET /users/me/preferences response type. */
export type GetUserPreferencesResp = {
  preferences: Partial<UserPreferences>;
};

/** This must be a valid surface name. */
export const preferenceUpdatePathParamsSchema = z.object({
  surface: z.enum(allSurfaces),
});

/**
 * Create a generic object schema. This should be the preference delta for a single surface.
 *
 * Full validation happens in updateUserPreferences, so we keep this simple and validate any object.
 */
export const preferenceUpdateBodySchema = z.object({}).catchall(z.any());

/** POST /users/me/preferences/:surface response type. */
export type UpdateSurfacePreferencesResp = {
  surfacePreferences: Partial<UserPreferences>;
};
